import {
  Component,
  input,
  output,
  signal,
  computed,
  effect,
  ChangeDetectionStrategy,
} from '@angular/core';

import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { FormsModule } from '@angular/forms';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TwilioGatewayConfig } from '@tourfold/libraries/tf-api-client-public-v2';
import { TranslateModule } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { SignalFormServerErrors } from '@tourfold/libraries/shared-util';

interface TwilioFormModel {
  name: string;
  enabled: boolean;
  username: string;
  accountSid: string;
  authToken: string;
  accountAuthToken: string;
  messagingServiceSid: string;
  phoneNumber: string;
}

const EMPTY_TWILIO_FORM: TwilioFormModel = {
  name: '',
  enabled: true,
  username: '',
  accountSid: '',
  authToken: '',
  accountAuthToken: '',
  messagingServiceSid: '',
  phoneNumber: '',
};

const TWILIO_FORM_FIELDS = [
  'name',
  'username',
  'accountSid',
  'authToken',
  'accountAuthToken',
  'messagingServiceSid',
  'phoneNumber',
] as const satisfies readonly (keyof TwilioFormModel)[];

export type TwilioSavePayload = {
  name: string;
  enabled: boolean;
  config: TwilioGatewayConfig;
};

@Component({
  selector: 'tf-settings-twilio-config-dialog',
  imports: [
    CommonModule,
    DialogModule,
    ButtonModule,
    InputTextModule,
    FormsModule,
    ToggleSwitchModule,
    TranslateModule,
  ],
  templateUrl: './twilio-config-dialog.html',
  styleUrl: './twilio-config-dialog.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TwilioConfigDialog {
  readonly visible = input<boolean>(false);
  readonly isEditingSystem = input<boolean>(false);
  readonly initialName = input<string>('');
  readonly initialEnabled = input<boolean>(true);
  readonly initialConfig = input<Partial<TwilioGatewayConfig> | null>(null);
  readonly onSave = input.required<(payload: TwilioSavePayload) => Promise<void>>();

  readonly visibleChange = output<boolean>();

  readonly formModel = signal<TwilioFormModel>({ ...EMPTY_TWILIO_FORM });
  readonly isSaving = signal<boolean>(false);

  readonly serverErrors = new SignalFormServerErrors(this.formModel, TWILIO_FORM_FIELDS, {
    pointerToField: {
      'twilioConfig/username': 'username',
      'twilioConfig/accountSid': 'accountSid',
      'twilioConfig/authToken': 'authToken',
      'twilioConfig/accountAuthToken': 'accountAuthToken',
      'twilioConfig/messagingServiceSid': 'messagingServiceSid',
      'twilioConfig/phoneNumber': 'phoneNumber',
    },
  });

  readonly isValid = computed(() => {
    const m = this.formModel();
    const hasName = m.name.trim().length > 0;
    const hasRequiredFields = !!(m.username && m.accountSid && m.authToken && m.accountAuthToken);
    return hasName && hasRequiredFields;
  });

  constructor() {
    // Re-seed the form model whenever the dialog opens.
    effect(() => {
      const initial = this.initialConfig();
      const name = this.initialName();
      const enabled = this.initialEnabled();
      const isVisible = this.visible();
      if (isVisible) {
        this.formModel.set({
          name: name ?? '',
          enabled: enabled ?? true,
          username: initial?.username ?? '',
          accountSid: initial?.accountSid ?? '',
          authToken: initial?.authToken ?? '',
          accountAuthToken: initial?.accountAuthToken ?? '',
          messagingServiceSid: initial?.messagingServiceSid ?? '',
          phoneNumber: initial?.phoneNumber ?? '',
        });
        this.serverErrors.clear();
      }
    });
  }

  onVisibleChange(visible: boolean): void {
    if (!visible) {
      this.formModel.set({ ...EMPTY_TWILIO_FORM });
      this.serverErrors.clear();
    }
    this.visibleChange.emit(visible);
  }

  updateField<K extends keyof TwilioFormModel>(field: K, value: TwilioFormModel[K]): void {
    this.formModel.update((current) => ({ ...current, [field]: value }));
  }

  async handleSave(): Promise<void> {
    if (!this.isValid() || this.isSaving()) {
      return;
    }
    const m = this.formModel();
    const payload: TwilioSavePayload = {
      name: m.name,
      enabled: m.enabled,
      config: {
        username: m.username,
        accountSid: m.accountSid,
        authToken: m.authToken,
        accountAuthToken: m.accountAuthToken,
        messagingServiceSid: m.messagingServiceSid,
        phoneNumber: m.phoneNumber,
      },
    };

    this.isSaving.set(true);
    this.serverErrors.clear();
    try {
      await this.onSave()(payload);
      // success: parent closes the dialog via [visible]
    } catch (err) {
      await this.serverErrors.apply(err);
    } finally {
      this.isSaving.set(false);
    }
  }

  onCancel(): void {
    if (this.isSaving()) return;
    this.onVisibleChange(false);
  }
}
